/**
 * Products Query Hooks
 * 封裝產品相關的查詢 hooks（使用 SWR）
 */

import {
  useProductsList,
  useProductsGet,
  useProductsGetCategories,
  type ProductsListParams,
} from '../../api/posClient';
import { swrListConfig, swrDetailConfig } from '../../lib/swr-config';
import type { SWRConfiguration } from 'swr';

/** SWR 選項（含 SDK 擴充的 enabled / swrKey） */
type SWROptionsWithEnabled = SWRConfiguration & {
  enabled?: boolean;
  swrKey?: string | null;
};

/**
 * 取得產品列表
 * 適用於儀表板、管理頁面等需要即時更新的場景
 */
export function useProducts(
  params?: ProductsListParams,
  options?: {
    fetch?: RequestInit;
    /**
     * 是否在視窗焦點時重新獲取資料（預設：true，適合列表/儀表板）
     */
    revalidateOnFocus?: boolean;
    /**
     * 去重複請求間隔（毫秒，預設：1 秒）
     */
    dedupingInterval?: number;
    // 允許傳入其他 SWR 選項（含 SDK 的 enabled、swrKey）
    swr?: SWROptionsWithEnabled;
  }
) {
  const {
    revalidateOnFocus = true,
    dedupingInterval = 1000,
    swr: swrOptions,
  } = options || {};

  const result = useProductsList(params, {
    fetch: options?.fetch,
    swr: {
      ...swrListConfig,
      revalidateOnFocus,
      dedupingInterval,
      ...swrOptions,
    },
  });

  // 提取實際資料
  const productsData = result.data?.data;
  const products =
    productsData && 'data' in productsData && Array.isArray(productsData.data)
      ? productsData.data
      : undefined;

  return {
    products,
    isLoading: result.isLoading,
    isError: !!result.error,
    error: result.error,
    mutate: result.mutate,
    isValidating: result.isValidating,
  };
}

/**
 * 取得單一產品詳情
 */
export function useProduct(
  id: string | undefined,
  options?: {
    fetch?: RequestInit;
    /**
     * 是否在視窗焦點時重新獲取資料（預設：false，詳情頁通常不需要）
     */
    revalidateOnFocus?: boolean;
    /**
     * 去重複請求間隔（毫秒，預設：5 秒）
     */
    dedupingInterval?: number;
    // 允許傳入其他 SWR 選項
    swr?: SWRConfiguration;
  }
) {
  const {
    revalidateOnFocus = false,
    dedupingInterval = 5000,
    swr: swrOptions,
  } = options || {};

  const result = useProductsGet(id ?? '', {
    fetch: options?.fetch,
    swr: {
      ...swrDetailConfig,
      revalidateOnFocus,
      dedupingInterval,
      enabled: !!id,
      ...swrOptions,
    },
  });

  // 提取實際資料
  const productData = result.data?.data;
  const product =
    productData && 'data' in productData ? productData.data : undefined;

  return {
    product,
    isLoading: result.isLoading,
    isError: !!result.error,
    error: result.error,
    mutate: result.mutate,
    isValidating: result.isValidating,
  };
}

/**
 * 取得產品分類列表
 * 分類資料變動較少，使用較長的快取時間
 */
export function useProductCategories(options?: {
  fetch?: RequestInit;
  /**
   * 是否在視窗焦點時重新獲取資料（預設：false）
   */
  revalidateOnFocus?: boolean;
  /**
   * 去重複請求間隔（毫秒，預設：10 秒）
   */
  dedupingInterval?: number;
  // 允許傳入其他 SWR 選項
  swr?: SWRConfiguration;
}) {
  const {
    revalidateOnFocus = false,
    dedupingInterval = 10000,
    swr: swrOptions,
  } = options || {};

  const result = useProductsGetCategories({
    fetch: options?.fetch,
    swr: {
      ...swrDetailConfig,
      revalidateOnFocus,
      dedupingInterval,
      ...swrOptions,
    },
  });

  // 提取實際資料
  const categoriesData = result.data?.data;
  const categories =
    categoriesData &&
    'data' in categoriesData &&
    Array.isArray(categoriesData.data)
      ? categoriesData.data
      : undefined;

  return {
    categories,
    isLoading: result.isLoading,
    isError: !!result.error,
    error: result.error,
    mutate: result.mutate,
    isValidating: result.isValidating,
  };
}
